import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Search, RefreshCw, AlertCircle, CheckCircle, Clock, Database } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface FrankLog {
  id: string;
  correlation_id: string;
  organization_id?: string;
  member_id?: string;
  association_id?: number;
  question?: string;
  language?: string;
  api_url?: string;
  success: boolean;
  response_time_ms?: number;
  answer?: string;
  error_type?: string;
  error_code?: number;
  error_message?: string;
  created_at: string;
  is_mobile?: boolean;
  network_type?: string;
}

const FrankLogsViewer = () => {
  const [logs, setLogs] = useState<FrankLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>('all');
  const [selectedLog, setSelectedLog] = useState<FrankLog | null>(null);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('frank_interaction_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (errorTypeFilter !== 'all') {
        if (errorTypeFilter === 'success') {
          query = query.eq('success', true);
        } else if (errorTypeFilter === 'error') {
          query = query.eq('success', false);
        } else {
          query = query.eq('error_type', errorTypeFilter);
        }
      }

      const { data, error } = await query;

      if (error) {
        toast.error('Failed to load Frank logs');
        console.error('Error loading logs:', error);
        return;
      }

      setLogs(data || []);
    } catch (error) {
      toast.error('Error loading logs');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [errorTypeFilter]);

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.question?.toLowerCase().includes(searchLower) ||
      log.error_message?.toLowerCase().includes(searchLower) ||
      log.correlation_id.toLowerCase().includes(searchLower) ||
      log.association_id?.toString().includes(searchLower)
    );
  });

  const ErrorTypeBadge = ({ errorType, success }: { errorType?: string; success: boolean }) => {
    if (success) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Success
        </Badge>
      );
    }

    const getErrorColor = (type?: string) => {
      switch (type) {
        case 'TIMEOUT': return 'bg-orange-100 text-orange-800';
        case 'NETWORK_ERROR': return 'bg-red-100 text-red-800';
        case 'CORS_ERROR': return 'bg-purple-100 text-purple-800';
        case 'API_DOWN': return 'bg-red-100 text-red-800';
        case 'INVALID_CONFIG': return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <Badge variant="secondary" className={getErrorColor(errorType)}>
        <AlertCircle className="w-3 h-3 mr-1" />
        {errorType || 'Error'}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Frank Interaction Logs
          </CardTitle>
          <CardDescription>
            Monitor Frank AI requests, responses, and errors across all organizations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search logs (question, error, correlation ID, association ID...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={errorTypeFilter} onValueChange={setErrorTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Logs</SelectItem>
                  <SelectItem value="success">Success Only</SelectItem>
                  <SelectItem value="error">Errors Only</SelectItem>
                  <SelectItem value="TIMEOUT">Timeouts</SelectItem>
                  <SelectItem value="NETWORK_ERROR">Network Errors</SelectItem>
                  <SelectItem value="CORS_ERROR">CORS Errors</SelectItem>
                  <SelectItem value="API_DOWN">API Down</SelectItem>
                  <SelectItem value="INVALID_CONFIG">Config Errors</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadLogs} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Logs Table */}
          <ScrollArea className="h-[600px] border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Association</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead>Response Time</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow
                    key={log.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedLog(log)}
                  >
                    <TableCell>
                      <ErrorTypeBadge errorType={log.error_type} success={log.success} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.association_id || 'N/A'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {log.question || (log.error_message ? 'Failed request' : 'N/A')}
                    </TableCell>
                    <TableCell>
                      {log.response_time_ms ? (
                        <span className={log.response_time_ms > 5000 ? 'text-orange-600' : ''}>
                          {log.response_time_ms}ms
                        </span>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        {log.is_mobile && <Badge variant="outline" className="text-xs">Mobile</Badge>}
                        {log.language && log.language !== 'en' && (
                          <Badge variant="outline" className="text-xs">{log.language.toUpperCase()}</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>

          {filteredLogs.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              No logs found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Details Modal/Panel */}
      {selectedLog && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Log Details</span>
              <Button variant="outline" size="sm" onClick={() => setSelectedLog(null)}>
                Close
              </Button>
            </CardTitle>
            <CardDescription>
              Correlation ID: {selectedLog.correlation_id}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Status:</strong>
                <div className="mt-1">
                  <ErrorTypeBadge errorType={selectedLog.error_type} success={selectedLog.success} />
                </div>
              </div>
              <div>
                <strong>Association ID:</strong>
                <p className="text-muted-foreground">{selectedLog.association_id || 'N/A'}</p>
              </div>
              <div>
                <strong>Response Time:</strong>
                <p className="text-muted-foreground">{selectedLog.response_time_ms || 'N/A'}ms</p>
              </div>
              <div>
                <strong>Language:</strong>
                <p className="text-muted-foreground">{selectedLog.language || 'N/A'}</p>
              </div>
            </div>

            {selectedLog.question && (
              <div>
                <strong>Question:</strong>
                <p className="text-muted-foreground mt-1 p-2 bg-muted rounded">{selectedLog.question}</p>
              </div>
            )}

            {selectedLog.answer && (
              <div>
                <strong>Answer:</strong>
                <ScrollArea className="h-32 mt-1 p-2 bg-muted rounded">
                  <p className="text-muted-foreground text-sm">{selectedLog.answer}</p>
                </ScrollArea>
              </div>
            )}

            {selectedLog.error_message && (
              <div>
                <strong>Error Message:</strong>
                <p className="text-red-600 mt-1 p-2 bg-red-50 rounded text-sm">{selectedLog.error_message}</p>
              </div>
            )}

            {selectedLog.api_url && (
              <div>
                <strong>API URL:</strong>
                <p className="text-muted-foreground font-mono text-xs break-all">{selectedLog.api_url}</p>
              </div>
            )}

            <Separator />
            
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Created:</strong> {new Date(selectedLog.created_at).toLocaleString()}</p>
              <p><strong>Device:</strong> {selectedLog.is_mobile ? 'Mobile' : 'Desktop'}</p>
              <p><strong>Network:</strong> {selectedLog.network_type || 'Unknown'}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FrankLogsViewer;